import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Typography,
} from '@mui/material';
import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { VersionChip } from '~/components/Chip/VersionChip';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';

type ModelSelectProps = {
    availableModels?: PredictionModel[];
    isLoadingModels: boolean;
};

export default function ModelSelect({
    availableModels,
    isLoadingModels,
}: ModelSelectProps) {
    const [dosePredictionTask, setDosePredictionTaskState] = useRecoilState(
        dosePredictionTaskState
    );

    const handleModelSelection = useCallback(
        (e: { target: { value: string } }) => {
            const selectedModelName = e.target.value;
            const selectedModel = availableModels?.find((model) =>
                model.customizations?.find((c) => c.name === selectedModelName)
            );

            if (selectedModel) {
                setDosePredictionTaskState((prev) => ({
                    ...prev,
                    model: {
                        version: selectedModel.version,
                        isLatest: selectedModel.isLatest,
                        name: selectedModelName,
                        label:
                            selectedModel.customizations?.find(
                                (c) => c.name === selectedModelName
                            )?.label ?? 'unknown',
                    },
                }));
            }
        },
        [availableModels, setDosePredictionTaskState]
    );

    if (isLoadingModels) {
        return <Skeleton width="100%" height={40} />;
    }

    return (
        <FormControl
            fullWidth
            size="small"
            sx={{
                '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                        borderColor: !dosePredictionTask?.model
                            ? 'warning.main'
                            : 'primary.main',
                    },
                    '&:hover fieldset': {
                        borderColor: !dosePredictionTask?.model
                            ? 'warning.dark'
                            : 'primary.dark',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: !dosePredictionTask?.model
                            ? 'warning.dark'
                            : 'primary.dark',
                    },
                },
                '& .MuiInputLabel-root': {
                    color: !dosePredictionTask?.model
                        ? 'warning.main'
                        : 'primary.main',
                },
            }}
        >
            <InputLabel id="prediction-model-select-label" shrink>
                Model
            </InputLabel>
            <Select
                data-testid="dosePredictionModelSelect"
                labelId="prediction-model-select-label"
                label="Model"
                value={dosePredictionTask?.model?.name ?? ''}
                onChange={handleModelSelection}
                displayEmpty
                notched
                renderValue={(selected) => {
                    if (!selected) {
                        return (
                            <Typography fontSize={14} color="warning.main">
                                Choose a prediction model
                            </Typography>
                        );
                    }
                    return (
                        <Box
                            display="grid"
                            gridTemplateColumns="1fr auto"
                            alignItems={'center'}
                            justifyContent="space-between"
                            gap={2}
                            width={'100%'}
                        >
                            <Typography fontSize={14}>
                                {dosePredictionTask.model?.label}
                            </Typography>
                            {dosePredictionTask.model?.version && (
                                <VersionChip
                                    version={dosePredictionTask.model.version}
                                    isLatest={dosePredictionTask.model.isLatest}
                                />
                            )}
                        </Box>
                    );
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            mt: 0.5,
                        },
                    },
                }}
            >
                {availableModels?.flatMap((model) =>
                    model.customizations?.map((customization) => (
                        <MenuItem
                            key={customization.name}
                            value={customization.name}
                        >
                            <Box
                                display="grid"
                                gridTemplateColumns="1fr auto"
                                alignItems={'center'}
                                justifyContent="space-between"
                                gap={2}
                                width={'100%'}
                            >
                                <Typography fontSize={14}>
                                    {customization.label}
                                </Typography>
                                {model.version && (
                                    <VersionChip
                                        version={model.version}
                                        isLatest={model.isLatest}
                                    />
                                )}
                            </Box>
                        </MenuItem>
                    ))
                )}
            </Select>
        </FormControl>
    );
}
